import React from 'react'
import { motion } from 'framer-motion'
import Button from '@/atoms/Button/Button';
import Icon from '@/atoms/Icon/Icon';
import WestholmeLogo from '@/atoms/Logo/WestholmeLogo';
import Text from '@/atoms/Text/Text';
import CtfCta, { CtaWrapper } from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media';
import { analytics, getClickHeaderNavigation, getClickCtaDetail } from '@/utils/analytics';
import { getContent } from '@/utils/helper';
import cn from 'class-names';

function NavigationWestholme({ fields, entries, hasBackground, navigationColor }) {
  const [openMenu, setOpenMenu] = React.useState(false);
  const [hideHome, setHideHome] = React.useState(false);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = React.useState(null);
  const { links } = fields;

  const navRef = React.useRef();

  const navigationMenu =
    links?.length > 0
      ? links.map((link) => {
          const menuContent = getContent(link.sys.id, entries)?.fields;

          const navigationContent = menuContent.contents?.map((item) => {
            const content = getContent(item.sys.id, entries)?.fields;
            const cta = getContent(content.callToAction.sys.id, entries)?.fields;
            return {
              ...content,
              cta: cta,
            }
          });

          // get page preview & url per link
          const page =
            menuContent?.pages && menuContent?.pages.length
              ? getContent(menuContent?.pages[0].sys.id, entries)
              : menuContent?.page
              ? getContent(menuContent?.page?.sys?.id, entries)
              : null;
          return {
            ...menuContent,
            pageUrl: page?.fields?.slug,
            navigationContent: navigationContent,
          };
        })
      : [];

  const leftNavigation = navigationMenu.filter((link) => link.pageUrl !== 'contact' && link.pageUrl !== 'sign-up')
  const rightNavigation = navigationMenu.filter((link) => link.pageUrl === 'contact' || link.pageUrl === 'sign-up')
  const mobileNavigation = navigationMenu.filter((link) => link.pageUrl !== 'home')

  React.useEffect(() => {
    if (openMenu) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }
  }, [openMenu]);

  React.useEffect(() => {
    if (window?.location) {
      if(window.location.pathname === '/' || window.location.pathname === '/home') {
        setHideHome(true);
      }
    }
  }, []);

  const handleMenuClick = (e, url, reloadPages) => {
    if(reloadPages) {
      window.location.href = url;
      analytics({
        window,
        document,
        detail: getClickHeaderNavigation(e, { url }),
      });
      return;
    }

    analytics({
      window,
      document,
      detail: getClickHeaderNavigation(e, { url }),
    });
    setOpenMenu(false);
    setActiveMobileSubmenu(null);
  };

  const handleMobileSubmenuToggle = (menuName) => {
    setActiveMobileSubmenu(activeMobileSubmenu === menuName ? null : menuName);
  };

  const subMenuAnimate = {
    enter: {
      opacity: 1,
      rotateX: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      },
      display: "block",
    },
    exit: {
      opacity: 0,
      rotateX: -10,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      },
      transitionEnd: {
        display: "none",
      },
    },
  };

  const ButtonCta = ({ name, url, page, idx, lengthItem, reloadPages, showMeganav, subNavigationContent, subNavigationHeader, subNavigationCta, navigationContent }) => {
    const [isHover, setIsHover] = React.useState(false);
    const hasSubMenu = navigationContent && navigationContent.length >= 2 && showMeganav;

    return (
      <motion.div
        className='inline-flex items-center group'
        key={idx}
        onHoverStart={() => hasSubMenu && setIsHover(true)}
        onHoverEnd={() => hasSubMenu && setIsHover(false)}
      >
        <div className='inline-flex items-center h-16 md:-14 hover:cursor-pointer z-1'>
          <CtaWrapper
            url={url}
            page={page}
            onClick={(e) => handleMenuClick(e, url ?? `/${page}`, reloadPages)}
          >
            <Text
              tag="body"
              style={cn(
                'text-black border-black uppercase group-hover:underline group-hover:underline-offset-[5px] decoration-[1px] decoration-black px-8', {
                  'border-r-1': idx !== lengthItem - 1,
                  'text-white border-white decoration-white': hasBackground || openMenu,
                  'text-black border-black decoration-black': !hasBackground && !openMenu,
                }
              )}
            >
              {name === 'Home' ? 'Westholme' : name}
            </Text>
          </CtaWrapper>
        </div>

        {hasSubMenu && (
          <motion.div
            ref={navRef}
            className={cn(
              'absolute left-0 w-full px-4 md:px-16 bg-white z-50',
              'overflow-y-auto',
              'max-h-[90vh]',
              'lg:overflow-hidden',
              'lg:max-h-[406px]',
              'top-[57px]'
            )}
            initial="exit"
            animate={isHover ? "enter" : "exit"}
            variants={subMenuAnimate}
          >
            <div className={cn(
              'relative max-w-[1408px] py-16 my-0 mx-auto grid',
              {
                'grid-cols-3 gap-10 lg:grid-cols-5 lg:gap-5': navigationContent.length === 2 || navigationContent.length === 4,
                'grid-cols-4 gap-5': navigationContent.length === 3,
              }
            )}>
              <div className="flex flex-col h-full justify-between">
                <Text tag="h5">
                  {subNavigationHeader}
                </Text>
                {subNavigationContent && (
                  <Text tag="caption" style="mt-2 px-5 border-l-1 border-black">
                    {subNavigationContent}
                  </Text>
                )}
                <div className="flex-1" />
                {subNavigationCta && (
                  <div className="product-kind-cta mt-5 md:mt-10">
                    <CtfCta
                      field={subNavigationCta}
                      entries={entries}
                      onClick={(e, { localUrl, url, name }) =>
                        analytics({
                          window,
                          document,
                          detail: getClickCtaDetail(e, { localUrl, url, name }),
                        })
                      }
                    />
                  </div>
                )}
              </div>
              {navigationContent.map((content, index) => (
                <div
                  className={cn(
                    "flex flex-col lg:col-start-auto", {
                      "col-start-2": index == 2,
                    }
                  )}
                  key={index}
                >
                  <DynamicAsset
                    assets={content.cloudinaryAssets}
                    entries={entries}
                    field={content.cloudinaryAssets}
                    container="container"
                    style={cn("lg:aspect-[59/41]", {
                      "aspect-[75/41]": navigationContent.length === 2 || navigationContent.length === 4,
                      "aspect-[59/41]": navigationContent.length === 3,
                    })}
                  />
                  <Text tag="h6" style="mt-10">
                    {content.title}
                  </Text>
                  <CtaWrapper
                    url={content?.cta?.link}
                    page={content.page}
                    onClick={(e) => handleMenuClick(e, content?.cta?.link ?? `/${content.page}`, content.reloadPages)}
                  >
                    <Text tag="caption" style="mt-5 px-5 border-l-1 border-black text-black uppercase hover:underline decoration-[1px] decoration-black">
                      {content?.cta?.name}
                    </Text>
                  </CtaWrapper>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  }

  const MobileMenuItem = ({ menu }) => {
    const hasSubMenu = menu.navigationContent && menu.navigationContent.length >= 2 && menu.showMeganav;

    return (
      <li className="nav-wh-item py-6" key={menu?.name}>
        <div className="flex items-center justify-between">
          <CtaWrapper
            url={menu?.pageUrl}
            page={menu?.pageUrl}
            onClick={(e) => handleMenuClick(e, menu?.pageUrl ?? `/${menu?.page}`, menu.reloadPage)}
          >
            <Text
              tag="h4-mobile"
              style="text-white uppercase"
            >
              {menu?.name}
            </Text>
          </CtaWrapper>

          {hasSubMenu && (
            <Button
              style="text-white p-2"
              onClick={() => handleMobileSubmenuToggle(menu.name)}
            >
              <Icon
                className="text-white"
                name="chevron-right"
              />
            </Button>
          )}
        </div>
      </li>
    );
  };

  return (
    <nav className={cn(
      `aaco-navigation-westholme transition-all duration-500 ease-in-out bg-${navigationColor} relative h-[76px] md:h-[57px] w-full z-40`,
      {
        '!bg-black transition-all duration-500 ease-in-out': openMenu,
      }
    )}>
      <div className="navigation-wrapper-wh container h-[76px] md:h-[57px] md:min-h-0 my-0 mx-auto">
        <div className="aaco-navigation-westholme-wrapper h-16 md:h-14 flex flex-row justify-between ">
          <div className="aaco-navigation-westholme-mobile-logo md:hidden block">
            <CtaWrapper
              page="/"
              classNames={cn("h-18 -ml-2.5", {
                'hidden': !openMenu && hideHome,
              })}
              onClick={(e) => handleMenuClick(e, `/`)}
            >
              <WestholmeLogo
                color={hasBackground || openMenu ? 'white' : 'black'}
                width={150}
                height={75}
              />
            </CtaWrapper>
          </div>
          <div className="aaco-navigation-westholme-hamburger-icon md:hidden block self-center mt-4">
            <Button
              style={`hamburger-menu cursor-pointer ${
                hasBackground || openMenu ? 'text-white' : 'text-black'}
              `}
              onClick={() => setOpenMenu(!openMenu)}
            >
              <Icon name={openMenu ? "cross" : "hamburger"} height={20} width={20} />
            </Button>
          </div>
          <div className="aaco-links-list hidden md:flex flex-row">
            {!hideHome && leftNavigation.map((m, idx) => (
              <ButtonCta
                key={`left-${idx}`}
                name={m.name}
                url={m?.url}
                page={m?.pageUrl}
                idx={idx}
                lengthItem={leftNavigation.length}
                reloadPages={m.reloadPage}
                showMeganav={m.showMeganav || false}
                subNavigationContent={m.subNavigationContent}
                subNavigationHeader={m.subNavigationHeader}
                subNavigationCta={m.subNavigationCta}
                navigationContent={m.navigationContent}
              />
            ))}
            {hideHome && leftNavigation.filter((m) => m.name !== 'Home').map((m, idx) => (
              <ButtonCta
                key={`left-filtered-${idx}`}
                name={m.name}
                url={m?.url}
                page={m?.pageUrl}
                idx={idx}
                lengthItem={leftNavigation.filter((m) => m.name !== 'Home').length}
                reloadPages={m.reloadPage}
                showMeganav={m.showMeganav || false}
                subNavigationContent={m.subNavigationContent}
                subNavigationHeader={m.subNavigationHeader}
                subNavigationCta={m.subNavigationCta}
                navigationContent={m.navigationContent}
              />
            ))}
          </div>
          <div className="aaco-links-list hidden md:flex flex-row">
            {rightNavigation.map((m, idx) => (
              <ButtonCta
                key={`right-${idx}`}
                name={m?.name}
                url={m?.url}
                page={m?.pageUrl}
                idx={idx}
                lengthItem={rightNavigation.length}
                reloadPages={m.reloadPage}
              />
            ))}
          </div>
        </div>
        <hr className={cn(
          'border-black', {
            'border-white': hasBackground,
          }
        )} />
      </div>
      <div className={cn(
        'absolute w-full bg-black -mt-[1px] block md:hidden transition-all duration-500 ease-in-out overflow-hidden', {
          'max-h-0 invisible' : !openMenu,
          'max-h-[100rem]': openMenu,
        }
      )}>
        <ul className="container my-0 mx-auto h-screen overflow-y-auto">
          {mobileNavigation.map((m, idx) => (
            <MobileMenuItem key={m?.name} menu={m} idx={idx} />
          ))}
        </ul>
      </div>

      {/* Mobile Submenu Overlay - Full Screen */}
      {activeMobileSubmenu && (
        <div className="fixed inset-0 bg-black z-50 md:hidden">
          <div className="container mx-auto h-full overflow-y-auto pb-7">
            {/* Header with back and close buttons */}
            <div className="flex items-center justify-between py-6 border-b border-white border-opacity-20">
              <Button
                style="text-white p-2 flex items-center"
                onClick={() => setActiveMobileSubmenu(null)}
              >
                <Icon
                  className="text-white"
                  name="chevron-left"
                />
              </Button>

              <Button
                style="text-white p-2"
                onClick={() => {
                  setActiveMobileSubmenu(null);
                  setOpenMenu(false);
                }}
              >
                <Icon name="close" className="w-6 h-6" />
              </Button>
            </div>

            {/* Submenu Content */}
            {(() => {
              const activeMenu = mobileNavigation.find(m => m.name === activeMobileSubmenu);
              if (!activeMenu) return null;

              return (
                <div className="py-8">
                  {/* Submenu header and content */}
                  <div className="mb-8">
                    <Text
                      tag="h5"
                      style={cn(
                        "text-white mb-4",
                      )}
                    >
                      {activeMenu.subNavigationHeader}
                    </Text>
                    {activeMenu.subNavigationContent && (
                      <Text
                        tag="caption"
                        style={cn(
                          "text-white opacity-80 pl-4 border-l-1 border-white mb-6 w-1/2",
                          "md:w-full"
                        )}
                      >
                        {activeMenu.subNavigationContent}
                      </Text>
                    )}
                    {activeMenu.subNavigationCta && (
                      <div className="mb-8 mt-5">
                        <CtfCta
                          field={activeMenu.subNavigationCta}
                          entries={entries}
                          noWrap={true}
                          className="sm:!w-auto"
                          isWhiteCta={true}
                          onClick={(e, { localUrl, url, name }) => {
                              setOpenMenu(false);
                              setActiveMobileSubmenu(null);
                              analytics({
                                window,
                                document,
                                detail: getClickCtaDetail(e, { localUrl, url, name }),
                              })
                            }
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Navigation content items - single column on mobile */}
                  <div className="grid grid-cols-1 gap-16">
                    {activeMenu.navigationContent?.map((content, index) => (
                      <div className="flex flex-col" key={index}>
                        <DynamicAsset
                          assets={content.cloudinaryAssets}
                          entries={entries}
                          field={content.cloudinaryAssets}
                          container="container"
                          style="aspect-[75/41] mb-6"
                        />
                        <Text tag="h6" style="text-white mb-5">
                          {content.title}
                        </Text>
                        <CtaWrapper
                          url={content?.cta?.link}
                          page={content.page}
                          onClick={(e) => handleMenuClick(e, content?.cta?.link ?? `/${content.page}`, content.reloadPages)}
                        >
                          <Text tag="caption" style="pl-4 border-l-1 border-white text-white uppercase hover:underline decoration-[1px] decoration-white">
                            {content?.cta?.name}
                          </Text>
                        </CtaWrapper>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </nav>
  )
}

export default NavigationWestholme
